import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  creaModello,
  aggiornaModello,
  getVariabiliModelli,
  previewModello,
} from "../utils/modelliFatturaApi";
import {
  TIPO_MODELLO_LABELS,
  FONTE_RIGA_LABELS,
  getSuggerimenti,
  applicaSuggerimento,
} from "../utils/variabiliFattura";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "./ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Loader2,
  Save,
  GripVertical,
  Sparkles,
  Eye,
  AlertTriangle,
  Check,
  HelpCircle,
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGIMI_IVA = [
  { value: "esente_art10", label: "Esente Art.10 DPR 633/72" },
  { value: "iva_4", label: "IVA 4%" },
  { value: "iva_22", label: "IVA 22%" },
  { value: "esclusa_art15", label: "Esclusa Art.15 DPR 633/72" },
];

const TIPI_MODELLO = [
  { value: "retta", label: "Retta" },
  { value: "servizi", label: "Servizi" },
  { value: "acconto", label: "Acconto" },
  { value: "saldo", label: "Saldo" },
  { value: "dimissione", label: "Dimissione" },
  { value: "altro", label: "Altro" },
];

const VISIBILITA_OPTIONS = [
  { value: "pubblico", label: "Pubblico (visibile a tutti nella struttura)" },
  { value: "privato", label: "Privato (solo io)" },
];

const FONTI_OPTIONS = Object.entries(FONTE_RIGA_LABELS).map(([value, label]) => ({ value, label }));

const OPERATORI = [
  { value: "==", label: "uguale a" },
  { value: "!=", label: "diverso da" },
  { value: ">", label: "maggiore di" },
  { value: "<", label: "minore di" },
  { value: "contains", label: "contiene" },
];

const CAMPI_CONDIZIONE = [
  { value: "primo_mese_ospite", label: "Primo mese ospite" },
  { value: "ospite_dimesso", label: "Ospite dimesso" },
  { value: "retta_maggiore_zero", label: "Retta > 0" },
  { value: "deposito_cauzionale_presente", label: "Deposito cauzionale presente" },
];

function newRiga() {
  return {
    id: `r${Date.now()}`,
    descrizione: "",
    prezzo_unitario: 0,
    quantita_default: 1,
    iva_id: "iva_22",
    regime_iva: "iva_22",
    fonte: "manuale",
    condizione_id: null,
    ordine: 0,
  };
}

function newCondizione() {
  return {
    id: `c${Date.now()}`,
    campo: "primo_mese_ospite",
    operatore: "==",
    valore: "true",
    descrizione: "",
  };
}

// Dialog che mostra tutte le variabili disponibili e permette di inserirle
function VariabiliDialog({ open, onClose, variabili, onInsert }) {
  const grouped = [
    { label: "Ospite", prefix: "ospite." },
    { label: "Periodo", prefix: "periodo." },
    { label: "Retta", prefix: "retta." },
    { label: "Struttura", prefix: "struttura." },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Variabili dinamiche disponibili
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">
          Clicca su una variabile per inserirla nel campo di testo corrente.
        </p>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {grouped.map((group) => {
            const items = variabili.filter((v) => v.chiave.startsWith(group.prefix));
            if (items.length === 0) return null;
            return (
              <div key={group.label}>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
                  {group.label}
                </p>
                <div className="space-y-1.5">
                  {items.map((v) => (
                    <button
                      key={v.chiave}
                      type="button"
                      onClick={() => { onInsert(`{{${v.chiave}}}`); onClose(); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left group"
                    >
                      <code className="text-[11px] bg-emerald-50 group-hover:bg-emerald-100 text-emerald-700 px-2 py-1 rounded font-mono shrink-0 border border-emerald-100">
                        {`{{${v.chiave}}}`}
                      </code>
                      <span className="text-xs text-slate-600 group-hover:text-slate-800">
                        {v.descrizione}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {/* Eventuali variabili non raggruppate */}
          {(() => {
            const known = grouped.flatMap((g) => variabili.filter((v) => v.chiave.startsWith(g.prefix)));
            const other = variabili.filter((v) => !known.includes(v));
            if (other.length === 0) return null;
            return (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Altre</p>
                <div className="space-y-1.5">
                  {other.map((v) => (
                    <button
                      key={v.chiave}
                      type="button"
                      onClick={() => { onInsert(`{{${v.chiave}}}`); onClose(); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left group"
                    >
                      <code className="text-[11px] bg-emerald-50 group-hover:bg-emerald-100 text-emerald-700 px-2 py-1 rounded font-mono shrink-0 border border-emerald-100">
                        {`{{${v.chiave}}}`}
                      </code>
                      <span className="text-xs text-slate-600 group-hover:text-slate-800">{v.descrizione}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Campo testo con autocomplete per variabili {{...}}
// onFocusRef: callback(insertFn) chiamata quando il campo riceve il focus —
//   insertFn(testo) inserisce testo alla posizione del cursore salvata più recentemente
function VarTextarea({ value, onChange, variabili = [], placeholder, rows = 2, className = "", onFocusRef }) {
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggerimenti, setSuggerimenti] = useState([]);
  const textareaRef = useRef(null);

  // Refs sempre aggiornati — evitano closure stantie nella funzione di inserimento
  const valueRef = useRef(value);
  const cursorPosRef = useRef(0);
  useEffect(() => { valueRef.current = value; }, [value]);

  // insertAtCursor ha identità stabile (nessuna dipendenza da value/cursorPos)
  // ma legge sempre i valori correnti tramite ref
  const insertAtCursor = useCallback((testo) => {
    const el = textareaRef.current;
    const pos = cursorPosRef.current;
    const currentValue = valueRef.current;
    const newValue = currentValue.slice(0, pos) + testo + currentValue.slice(pos);
    const newPos = pos + testo.length;
    onChange(newValue);
    setTimeout(() => {
      if (el) { el.focus(); el.setSelectionRange(newPos, newPos); }
    }, 0);
  }, [onChange]); // stabile: ricrea solo se onChange cambia

  // Registra la funzione di inserimento ogni volta che il campo riceve il focus
  const handleFocus = (e) => {
    cursorPosRef.current = e.target.selectionStart ?? 0;
    if (onFocusRef) onFocusRef(insertAtCursor);
  };

  // Salva la posizione del cursore anche al blur, prima che il focus
  // passi al dialog e selectionStart diventi 0
  const handleBlur = (e) => {
    cursorPosRef.current = e.target.selectionStart ?? cursorPosRef.current;
    setTimeout(() => setShowSuggest(false), 150);
  };

  // Aggiorna posizione cursore ad ogni movimento
  const trackCursor = (e) => {
    cursorPosRef.current = e.target.selectionStart ?? cursorPosRef.current;
  };

  const handleChange = (e) => {
    const v = e.target.value;
    cursorPosRef.current = e.target.selectionStart;
    onChange(v);
    const sugg = getSuggerimenti(v, e.target.selectionStart, variabili);
    setSuggerimenti(sugg);
    setShowSuggest(sugg.length > 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "{" && textareaRef.current) {
      const pos = textareaRef.current.selectionStart + 1;
      setTimeout(() => {
        const cur = textareaRef.current?.value;
        const sugg = getSuggerimenti(cur, pos, variabili);
        setSuggerimenti(sugg);
        setShowSuggest(sugg.length > 0);
      }, 0);
    }
    if (e.key === "Escape") setShowSuggest(false);
  };

  const handleSuggerisci = (chiave) => {
    const pos = cursorPosRef.current;
    const { testo, cursorPos: newPos } = applicaSuggerimento(valueRef.current, pos, chiave);
    onChange(testo);
    cursorPosRef.current = newPos;
    setShowSuggest(false);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onSelect={trackCursor}
        onClick={trackCursor}
        placeholder={placeholder}
        rows={rows}
        className={`text-xs font-mono resize-none ${className}`}
      />
      {showSuggest && suggerimenti.length > 0 && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-56">
          {suggerimenti.map((v) => (
            <button
              key={v.chiave}
              type="button"
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
              onMouseDown={(e) => { e.preventDefault(); handleSuggerisci(v.chiave); }}
            >
              <code className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[10px]">
                {`{{${v.chiave}}}`}
              </code>
              <span className="text-slate-500">{v.descrizione}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ModelloFatturaEditor({ open, modello, onClose, onSave }) {
  const { token } = useAuth();
  const [tab, setTab] = useState("generale");
  const [saving, setSaving] = useState(false);
  const [variabili, setVariabili] = useState([]);

  // Form state
  const [nome, setNome] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [tipo, setTipo] = useState("altro");
  const [visibilita, setVisibilita] = useState("privato");
  const [causale, setCausale] = useState("");
  const [noteDefault, setNoteDefault] = useState("");
  const [scadenzaOffset, setScadenzaOffset] = useState(30);
  const [riferimentoOspiteSeDiverso, setRiferimentoOspiteSeDiverso] = useState(true);
  const [riferimentoOspiteTemplate, setRiferimentoOspiteTemplate] = useState("{{ospite.riferimento}}");
  const [righe, setRighe] = useState([newRiga()]);
  const [condizioni, setCondizioni] = useState([]);

  // Preview state
  const [previewFascicoloId, setPreviewFascicoloId] = useState("");
  const [previewMese, setPreviewMese] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [fascicoli, setFascicoli] = useState([]);

  // Dialog variabili
  const [varDialogOpen, setVarDialogOpen] = useState(false);
  const activeInsertFnRef = useRef(null); // tiene l'insertFn dell'ultimo VarTextarea focalizzato

  // Carica variabili disponibili
  useEffect(() => {
    if (!token || !open) return;
    getVariabiliModelli(token)
      .then((res) => setVariabili(res.variabili || []))
      .catch(() => {});
  }, [token, open]);

  // Carica fascicoli per preview (solo attivi: esclusi archiviati)
  useEffect(() => {
    if (!token || !open) return;
    axios
      .get(`${API_URL}/api/fascicoli-assistenziali`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 200 },
      })
      .then((res) => {
        const list = res.data?.fascicoli || res.data || [];
        const attivi = (Array.isArray(list) ? list : []).filter(
          (f) => f.archiviata !== true,
        );
        setFascicoli(attivi);
        setPreviewFascicoloId((prev) => {
          if (!prev) return prev;
          const stillThere = attivi.some((f) => (f._id || f.id) === prev);
          return stillThere ? prev : "";
        });
      })
      .catch(() => {});
  }, [token, open]);

  // Inizializza il form quando si apre l'editor
  useEffect(() => {
    if (!open) return;
    setTab("generale");
    setPreviewResult(null);
    if (modello) {
      setNome(modello.nome || "");
      setDescrizione(modello.descrizione || "");
      setTipo(modello.tipo || "altro");
      setVisibilita(modello.visibilita || "privato");
      setCausale(modello.causale || "");
      setNoteDefault(modello.note_default || "");
      setScadenzaOffset(modello.scadenza_offset_giorni ?? 30);
      setRiferimentoOspiteSeDiverso(
        modello.riferimento_ospite_se_destinatario_diverso !== false,
      );
      setRiferimentoOspiteTemplate(
        modello.riferimento_ospite_template || "{{ospite.riferimento}}",
      );
      setRighe(
        modello.righe?.length > 0
          ? modello.righe.map((r) => ({ ...r }))
          : [newRiga()]
      );
      setCondizioni(modello.condizioni?.length > 0 ? modello.condizioni.map((c) => ({ ...c })) : []);
    } else {
      setNome("");
      setDescrizione("");
      setTipo("altro");
      setVisibilita("privato");
      setCausale("");
      setNoteDefault("");
      setScadenzaOffset(30);
      setRiferimentoOspiteSeDiverso(true);
      setRiferimentoOspiteTemplate("{{ospite.riferimento}}");
      setRighe([newRiga()]);
      setCondizioni([]);
    }
  }, [open, modello]);

  const handleSave = async () => {
    if (!nome.trim()) {
      toast.error("Il nome del modello è obbligatorio");
      setTab("generale");
      return;
    }
    setSaving(true);
    const payload = {
      nome: nome.trim(),
      descrizione: descrizione.trim() || null,
      tipo,
      visibilita,
      causale: causale.trim() || null,
      note_default: noteDefault.trim() || null,
      scadenza_offset_giorni: scadenzaOffset,
      riferimento_ospite_se_destinatario_diverso: riferimentoOspiteSeDiverso,
      riferimento_ospite_template: riferimentoOspiteTemplate.trim() || "{{ospite.riferimento}}",
      righe: righe.map((r, i) => ({ ...r, ordine: i })),
      condizioni,
    };
    try {
      if (modello?.id) {
        await aggiornaModello(token, modello.id, payload);
        toast.success("Modello aggiornato");
      } else {
        await creaModello(token, payload);
        toast.success("Modello creato");
      }
      onSave?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!modello?.id) {
      toast.info("Salva prima il modello per eseguire la preview");
      return;
    }
    if (!previewFascicoloId) {
      toast.error("Seleziona un ospite per la preview");
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await previewModello(token, modello.id, previewFascicoloId, previewMese);
      setPreviewResult(res);
    } catch {
      toast.error("Errore nel calcolo dell'anteprima");
    } finally {
      setPreviewLoading(false);
    }
  };

  const addRiga = () => setRighe((prev) => [...prev, newRiga()]);
  const removeRiga = (idx) => setRighe((prev) => prev.filter((_, i) => i !== idx));
  const updateRiga = (idx, field, value) =>
    setRighe((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const addCondizione = () => setCondizioni((prev) => [...prev, newCondizione()]);
  const removeCondizione = (idx) => setCondizioni((prev) => prev.filter((_, i) => i !== idx));
  const updateCondizione = (idx, field, value) =>
    setCondizioni((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  const isReadonly = modello?.is_base || modello?.origine === "fic";

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <SheetTitle className="text-base">
            {modello ? (isReadonly ? "Visualizza modello" : "Modifica modello") : "Nuovo modello"}
          </SheetTitle>
          {isReadonly && (
            <p className="text-xs text-amber-600 flex items-center gap-1.5 mt-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {modello?.is_base
                ? "I modelli base di Affidya non sono modificabili. Duplica per personalizzare."
                : "I modelli FIC sono in sola lettura. Duplica per personalizzare."}
            </p>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <Tabs value={tab} onValueChange={setTab}>
            <div className="px-6 pt-4 border-b border-slate-100">
              <TabsList className="h-8">
                <TabsTrigger value="generale" className="text-xs px-3">Generale</TabsTrigger>
                <TabsTrigger value="righe" className="text-xs px-3">
                  Righe
                  <Badge className="ml-1.5 text-[10px] px-1.5 py-0 bg-slate-100 text-slate-600 border-0">
                    {righe.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="condizioni" className="text-xs px-3">
                  Variabili & Condizioni
                </TabsTrigger>
                <TabsTrigger value="anteprima" className="text-xs px-3">
                  <Eye className="w-3 h-3 mr-1" />
                  Anteprima
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab Generale */}
            <TabsContent value="generale" className="p-6 space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs">Nome modello *</Label>
                  <Input
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Es. Retta mensile standard"
                    className="text-xs h-8"
                    disabled={isReadonly}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={tipo} onValueChange={setTipo} disabled={isReadonly}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPI_MODELLO.map((t) => (
                        <SelectItem key={t.value} value={t.value} className="text-xs">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Visibilità</Label>
                  <Select value={visibilita} onValueChange={setVisibilita} disabled={isReadonly}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VISIBILITA_OPTIONS.map((v) => (
                        <SelectItem key={v.value} value={v.value} className="text-xs">
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs">Descrizione</Label>
                  <Input
                    value={descrizione}
                    onChange={(e) => setDescrizione(e.target.value)}
                    placeholder="Descrizione opzionale"
                    className="text-xs h-8"
                    disabled={isReadonly}
                  />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1.5">
                      Causale
                      <span className="text-slate-400 font-normal">
                        — supporta variabili{" "}
                        <code className="bg-slate-100 px-1 rounded text-[10px]">{"{{...}}"}</code>
                      </span>
                    </Label>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-[10px] text-emerald-600 hover:text-emerald-800 hover:underline"
                      onClick={() => setVarDialogOpen(true)}
                    >
                      <HelpCircle className="w-3 h-3" />
                      Inserisci variabile
                    </button>
                  </div>
                  <VarTextarea
                    value={causale}
                    onChange={setCausale}
                    variabili={variabili}
                    placeholder="Es. Retta di degenza mese di {{periodo.mese_anno}}"
                    rows={2}
                    onFocusRef={(fn) => { activeInsertFnRef.current = fn; }}
                  />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Note predefinite</Label>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-[10px] text-emerald-600 hover:text-emerald-800 hover:underline"
                      onClick={() => setVarDialogOpen(true)}
                    >
                      <HelpCircle className="w-3 h-3" />
                      Inserisci variabile
                    </button>
                  </div>
                  <VarTextarea
                    value={noteDefault}
                    onChange={setNoteDefault}
                    variabili={variabili}
                    placeholder="Note visibili in fattura (opzionale)"
                    rows={2}
                    onFocusRef={(fn) => { activeInsertFnRef.current = fn; }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Scadenza (giorni dalla data fattura)</Label>
                  <Input
                    type="number"
                    value={scadenzaOffset}
                    onChange={(e) => setScadenzaOffset(parseInt(e.target.value) || 30)}
                    className="text-xs h-8 w-24"
                    min={0}
                    disabled={isReadonly}
                  />
                </div>
                <div className="col-span-2 rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={riferimentoOspiteSeDiverso}
                      onCheckedChange={(v) => setRiferimentoOspiteSeDiverso(v === true)}
                      disabled={isReadonly}
                      className="mt-0.5"
                    />
                    <span className="text-xs text-slate-700 leading-snug">
                      Se il <strong>destinatario</strong> è diverso dall&apos;ospite, aggiungi automaticamente
                      nelle note il riferimento{" "}
                      <code className="bg-white px-1 rounded text-[10px] border">Sig. / Sig.ra Nome Ospite</code>
                      {" "}(dal sesso in anagrafica)
                    </span>
                  </label>
                  {riferimentoOspiteSeDiverso && (
                    <div className="space-y-1.5 pl-6">
                      <Label className="text-[10px] text-slate-500">Template riferimento (variabili)</Label>
                      <VarTextarea
                        value={riferimentoOspiteTemplate}
                        onChange={setRiferimentoOspiteTemplate}
                        variabili={variabili}
                        placeholder="{{ospite.riferimento}}"
                        rows={1}
                        onFocusRef={(fn) => { activeInsertFnRef.current = fn; }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Tab Righe */}
            <TabsContent value="righe" className="p-6 mt-0 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  Definisci le righe della fattura. Le righe con fonte dinamica verranno
                  popolate automaticamente al momento della generazione.
                </p>
                {!isReadonly && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 shrink-0"
                    onClick={addRiga}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Aggiungi riga
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {righe.map((riga, idx) => (
                  <div key={riga.id} className="border border-slate-200 rounded-lg p-3 space-y-2.5 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                        <span className="text-[10px] font-medium text-slate-400">Riga {idx + 1}</span>
                        {riga.fonte !== "manuale" && (
                          <Badge className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-600 border-0">
                            Dinamica
                          </Badge>
                        )}
                      </div>
                      {!isReadonly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-slate-400 hover:text-red-500"
                          onClick={() => removeRiga(idx)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-[10px]">Descrizione</Label>
                          <button
                            type="button"
                            className="flex items-center gap-1 text-[10px] text-emerald-600 hover:text-emerald-800 hover:underline"
                            onClick={() => setVarDialogOpen(true)}
                          >
                            <HelpCircle className="w-3 h-3" />
                            Variabili
                          </button>
                        </div>
                        <VarTextarea
                          value={riga.descrizione}
                          onChange={(v) => updateRiga(idx, "descrizione", v)}
                          variabili={variabili}
                          placeholder="Es. Soggiorno Mese di {{periodo.mese_anno}}"
                          rows={1}
                          onFocusRef={(fn) => { activeInsertFnRef.current = fn; }}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Fonte dati</Label>
                        <Select
                          value={riga.fonte}
                          onValueChange={(v) => updateRiga(idx, "fonte", v)}
                          disabled={isReadonly}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FONTI_OPTIONS.map((f) => (
                              <SelectItem key={f.value} value={f.value} className="text-xs">
                                {f.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Regime IVA</Label>
                        <Select
                          value={riga.iva_id || riga.regime_iva || "iva_22"}
                          onValueChange={(v) => { updateRiga(idx, "iva_id", v); updateRiga(idx, "regime_iva", v); }}
                          disabled={isReadonly}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {REGIMI_IVA.map((r) => (
                              <SelectItem key={r.value} value={r.value} className="text-xs">
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {riga.fonte === "manuale" && (
                        <>
                          <div className="space-y-1">
                            <Label className="text-[10px]">Prezzo unitario (€)</Label>
                            <Input
                              type="number"
                              value={riga.prezzo_unitario}
                              onChange={(e) => updateRiga(idx, "prezzo_unitario", parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs"
                              step="0.01"
                              disabled={isReadonly}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px]">Quantità predefinita</Label>
                            <Input
                              type="number"
                              value={riga.quantita_default}
                              onChange={(e) => updateRiga(idx, "quantita_default", parseFloat(e.target.value) || 1)}
                              className="h-7 text-xs"
                              step="1"
                              min="1"
                              disabled={isReadonly}
                            />
                          </div>
                        </>
                      )}
                      {condizioni.length > 0 && (
                        <div className="col-span-2 space-y-1">
                          <Label className="text-[10px]">Condizione (lascia vuoto = sempre inclusa)</Label>
                          <Select
                            value={riga.condizione_id || "none"}
                            onValueChange={(v) => updateRiga(idx, "condizione_id", v === "none" ? null : v)}
                            disabled={isReadonly}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Nessuna condizione" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" className="text-xs">Sempre inclusa</SelectItem>
                              {condizioni.map((c) => (
                                <SelectItem key={c.id} value={c.id} className="text-xs">
                                  {c.descrizione || `${c.campo} ${c.operatore} ${c.valore}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {righe.length === 0 && (
                  <div className="text-center py-8 text-slate-400 text-xs">
                    Nessuna riga. Clicca "Aggiungi riga" per iniziare.
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Variabili & Condizioni */}
            <TabsContent value="condizioni" className="p-6 mt-0 space-y-6">
              {/* Variabili */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <h3 className="text-sm font-medium text-slate-800">Variabili disponibili</h3>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {variabili.map((v) => (
                    <div key={v.chiave} className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 border border-slate-100">
                      <code className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded shrink-0">
                        {`{{${v.chiave}}}`}
                      </code>
                      <span className="text-[10px] text-slate-500 leading-snug">{v.descrizione}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Condizioni */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-800">Condizioni per le righe</h3>
                  {!isReadonly && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={addCondizione}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Aggiungi condizione
                    </Button>
                  )}
                </div>

                {condizioni.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    Nessuna condizione definita. Le condizioni permettono di includere/escludere
                    righe in base a caratteristiche dell'ospite o del periodo.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {condizioni.map((c, idx) => (
                      <div key={c.id} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-white">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-medium text-slate-500">
                            Condizione ID: <code className="bg-slate-100 px-1 rounded">{c.id}</code>
                          </span>
                          {!isReadonly && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-slate-400 hover:text-red-500"
                              onClick={() => removeCondizione(idx)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="space-y-1">
                            <Label className="text-[10px]">Campo</Label>
                            <Select
                              value={c.campo}
                              onValueChange={(v) => updateCondizione(idx, "campo", v)}
                              disabled={isReadonly}
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CAMPI_CONDIZIONE.map((f) => (
                                  <SelectItem key={f.value} value={f.value} className="text-xs">
                                    {f.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px]">Operatore</Label>
                            <Select
                              value={c.operatore}
                              onValueChange={(v) => updateCondizione(idx, "operatore", v)}
                              disabled={isReadonly}
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {OPERATORI.map((o) => (
                                  <SelectItem key={o.value} value={o.value} className="text-xs">
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px]">Valore</Label>
                            <Input
                              value={c.valore}
                              onChange={(e) => updateCondizione(idx, "valore", e.target.value)}
                              className="h-7 text-xs"
                              disabled={isReadonly}
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px]">Etichetta (opzionale)</Label>
                          <Input
                            value={c.descrizione}
                            onChange={(e) => updateCondizione(idx, "descrizione", e.target.value)}
                            placeholder="Descrizione leggibile della condizione"
                            className="h-7 text-xs"
                            disabled={isReadonly}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Anteprima */}
            <TabsContent value="anteprima" className="p-6 mt-0 space-y-4">
              <p className="text-xs text-slate-500">
                Seleziona un ospite e un mese per vedere come verranno risolte le righe del modello.
                {!modello?.id && (
                  <span className="text-amber-600"> Salva prima il modello per abilitare la preview.</span>
                )}
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Ospite di test</Label>
                  <select
                    className="w-full h-8 rounded-md border border-slate-200 bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#4CAF50]/30"
                    value={previewFascicoloId}
                    onChange={(e) => setPreviewFascicoloId(e.target.value)}
                  >
                    <option value="">Seleziona ospite...</option>
                    {fascicoli.map((f) => (
                      <option key={f._id || f.id} value={f._id || f.id}>
                        {f.nome_completo ||
                          `${f.anagrafica?.cognome || ""} ${f.anagrafica?.nome || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Mese</Label>
                  <Input
                    type="month"
                    value={previewMese}
                    onChange={(e) => setPreviewMese(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              <Button
                type="button"
                size="sm"
                className="text-xs bg-[#4CAF50] hover:bg-[#388E3C]"
                onClick={handlePreview}
                disabled={previewLoading || !modello?.id}
              >
                {previewLoading ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                )}
                Calcola anteprima
              </Button>

              {previewResult && (
                <div className="border border-slate-200 rounded-lg bg-slate-50 p-4 space-y-3">
                  {previewResult.causale && (
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 mb-1">Causale risolta</p>
                      <p className="text-xs text-slate-800">{previewResult.causale}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] font-medium text-slate-500 mb-2">
                      Righe ({previewResult.righe?.length || 0})
                    </p>
                    {previewResult.righe?.length > 0 ? (
                      <div className="space-y-1.5">
                        {previewResult.righe.map((r, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between bg-white rounded px-3 py-2 border border-slate-100"
                          >
                            <span className="text-xs text-slate-700">{r.descrizione}</span>
                            <span className="text-xs font-medium text-slate-800 shrink-0 ml-4">
                              {r.quantita} × €{Number(r.prezzo_unitario).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400">Nessuna riga generata per questo ospite/mese.</p>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <SheetFooter className="px-6 py-4 border-t border-slate-100">
          <div className="flex items-center justify-between w-full">
            <Button variant="ghost" size="sm" className="text-xs" onClick={onClose}>
              {isReadonly ? "Chiudi" : "Annulla"}
            </Button>
            {!isReadonly && (
              <Button
                size="sm"
                className="text-xs bg-[#4CAF50] hover:bg-[#388E3C]"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                )}
                {modello?.id ? "Salva modifiche" : "Crea modello"}
              </Button>
            )}
          </div>
        </SheetFooter>
      </SheetContent>

      <VariabiliDialog
        open={varDialogOpen}
        onClose={() => setVarDialogOpen(false)}
        variabili={variabili}
        onInsert={(testo) => {
          if (activeInsertFnRef.current) {
            activeInsertFnRef.current(testo);
          }
        }}
      />
    </Sheet>
  );
}
